<template>
  <Sidebar
    width="60vw"
    :visible="visible"
    @update:visible="$emit('close')"
    className="user-note-sidebar"
  >
    <template #head-left>
      <h3>{{ $t("notes.title") }}</h3>
    </template>

    <template #body>
      <div class="notes-layout">
        <!-- LEFT: NOTE LIST -->
        <div class="note-list">
          <div class="note-list-header">
            <button class="btn-primary" id="createtask_driver" @click="addNote">
              + {{ $t("notes.create_new_note") }}
            </button>
          </div>

          <div
            v-for="note in notes"
            :key="note._id"
            class="note-item"
            :class="{ active: note._id === activeNoteId }"
            @click="activeNoteId = note._id"
          >
            <div class="note-title">
              <span v-if="note.is_pin">
                <img
                  :src="require('@/assets/images/pin_bold.svg')"
                  :alt="$t('notes.pin')"
                  class="note-item-pin-icon"
                />
              </span>
              {{ note.title || $t("notes.untitled") }}
            </div>
            <div class="note-preview">
              {{ previewText(note.content) }}
            </div>
          </div>
        </div>

        <!-- RIGHT: EDITOR -->
        <div class="note-editor">
          <div v-if="activeNote" class="editor-header">
            <div class="title-wrapper">
              <span v-if="!isEditingTitle">{{
                activeNote.title || $t("notes.untitled")
              }}</span>
              <input
                v-else
                type="text"
                v-model="activeNote.title"
                @blur="saveTitle"
                @keyup.enter="saveTitle"
                class="form-control"
                ref="titleInput"
              />
            </div>
            <div class="note-actions">
              <button
                type="button"
                class="note-action-button"
                :title="$t('notes.export_word_title')"
                :aria-label="$t('notes.export_word_title')"
                @click.stop="exportNoteToWord(activeNote)"
              >
                <img :src="wordIcon" alt="" />
              </button>
              <img
                @click.stop="toggleEditTitle"
                :src="require('@/assets/images/editgrey.png')"
                :alt="$t('Common.edit')"
                class="icon-edit"
              />
              <img
                :src="attachIcon"
                :alt="$t('notes.add_file')"
                class="icon-trash"
                @click.stop="openAddFile(activeNote)"
              />
              <img
                :src="require('@/assets/images/trash.svg')"
                :alt="$t('notes.delete')"
                class="icon-trash"
                @click.stop="deleteNote(activeNote)"
              />
              <img
                :src="require('@/assets/images/pin-icon.svg')"
                :alt="$t('notes.pin')"
                class="icon-trash"
                @click.stop="togglePin(activeNote)"
              />
            </div>
          </div>

          <Description
            ref="noteDescriptionRef"
            v-if="activeNote"
            :description="getNoteContentForEditor(activeNote)"
            :key="activeNote._id"
            :editPermission="true"
            from="user"
            :note="activeNote"
            @updated="updateNoteInLocal"
            @add-file="(files) => openAddFile(activeNote, files)"
          />
          <div v-if="!activeNote" class="empty-editor">
            {{ $t("notes.select_note") }}
          </div>
        </div>
      </div>
    </template>
  </Sidebar>

  <ConfirmModal
    v-model="showDeleteModal"
    :title="deleteModalTitle"
    :acceptButtonText="$t('notes.delete')"
    :cancelButtonText="$t('Common.cancel')"
    :showCloseIcon="false"
    :closeOnBackdrop="false"
    @accept="handleDeleteConfirm"
    @close="
      () => {
        showDeleteModal = false;
        noteToDelete = null;
      }
    "
  >
    <template #body>
      <span>{{ deleteModalMessage }}</span>
    </template>
  </ConfirmModal>
  <input
    ref="noteFileInput"
    type="file"
    class="d-none"
    multiple
    @change="handleNoteFileChange"
  />
</template>

<script setup>
import { ref, computed, watch, defineProps } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import Sidebar from "@/components/molecules/Sidebar/Sidebar.vue";
import Description from "@/components/atom/Description/Description.vue";
import { apiRequest } from "@/services";
import { nextTick } from "vue";
import ConfirmModal from "@/components/atom/Modal/Modal.vue";
import { checkFile, uploadToWasabi } from "@/views/Notes/helper";
import { exportNoteToWord } from "@/views/Notes/exportNoteToWord.cjs";
defineEmits(["close"]);
const { t } = useI18n();
// Title edit
const isEditingTitle = ref(false);
const titleInput = ref(null);
const noteFileInput = ref(null);
const noteDescriptionRef = ref(null);
const uploadingNoteFile = ref(false);
const fileTargetNoteId = ref(null);
const attachIcon = require("@/assets/images/footerAttachmenticon.svg");
const wordIcon = require("@/assets/images/svg/file_blue.svg");

function toggleEditTitle() {
  isEditingTitle.value = !isEditingTitle.value;
  if (isEditingTitle.value) nextTick(() => titleInput.value.focus());
}

async function saveTitle() {
  isEditingTitle.value = false;
  await updateNoteTitle(activeNote.value);
}

// Props
const props = defineProps({ visible: Boolean });

// Vuex
const store = useStore();
const currentUser = computed(() => store.getters["users/currentUser"]);

// Notes local
const notes = ref([]);
const activeNoteId = ref(null);
const activeNote = computed(() =>
  notes.value.find((n) => n._id === activeNoteId.value)
);

// Modal delete
const showDeleteModal = ref(false);
const noteToDelete = ref(null);
const deleteModalTitle = ref("");
const deleteModalMessage = ref(t("notes.delete_confirm_message"));

// Load notes when sidebar opens
watch(
  () => props.visible,
  async (visible) => {
    if (!visible || !currentUser.value?._id) return;

    const res = await apiRequest(
      "get",
      `/api/v1/notes?userId=${currentUser.value._id}`
    );
    notes.value = res?.data?.data || [];

    if (!notes.value.length) {
      const createRes = await apiRequest("post", "/api/v1/notes", {
        userId: currentUser.value._id,
        title: t("notes.default_note_name"),
        content: null,
      });
      const newNote = createRes?.data?.data;
      notes.value = [newNote];
      activeNoteId.value = newNote._id;
      window.dispatchEvent(
        new CustomEvent("note-created", {
          detail: { note: newNote },
        })
      );
      return;
    }

    const pinned = notes.value.find((n) => n.is_pin);
    activeNoteId.value = pinned?._id || notes.value[0]?._id;
  }
);

// Add note
async function addNote() {
  const res = await apiRequest("post", "/api/v1/notes", {
    userId: currentUser.value._id,
    title: t("notes.default_note_name"),
    content: null,
  });
  const newNote = res?.data?.data;
  if (!newNote) return;

  notes.value.unshift(newNote);
  notes.value.sort((a, b) => {
    if (a.is_pin && !b.is_pin) return -1;
    if (!a.is_pin && b.is_pin) return 1;
    return (
      new Date(b.updatedAt || b.createdAt) -
      new Date(a.updatedAt || a.createdAt)
    );
  });
  activeNoteId.value = newNote._id;
  window.dispatchEvent(
    new CustomEvent("note-created", {
      detail: { note: newNote },
    })
  );
}

// Preview
function previewText(content) {
  if (!content || !content.blocks) return "";
  const plainText = (block) => {
    if (block?.data?.text) return block.data.text;
    if (block?.type === "toggle")
      return block.data?.title || block.data?.content || "";
    if (block?.type === "page") return block.data?.title || "";
    if (block?.type === "callout" || block?.type === "quote")
      return block.data?.text || "";
    if (["list", "bulletedList", "numberedList"].includes(block?.type)) {
      const firstItem = block.data?.items?.[0];
      return typeof firstItem === "string"
        ? firstItem
        : firstItem?.content || "";
    }
    return "";
  };
  const text = content.blocks.map(plainText).find(Boolean) || "";
  return text.replace(/<[^>]*>/g, "").slice(0, 50);
}

// Update note local
function updateNoteInLocal(updatedNote) {
  const index = notes.value.findIndex((n) => n._id === updatedNote._id);
  if (index >= 0)
    notes.value[index] = { ...notes.value[index], ...updatedNote };
}

function openAddFile(note, files = null) {
  if (!note?._id || uploadingNoteFile.value) return;
  fileTargetNoteId.value = note._id;
  if (Array.isArray(files) && files.length) {
    uploadNoteFiles(files);
    return;
  }
  noteFileInput.value?.click();
}

function isEmptyFileBlock(block) {
  if (block?.type !== "file") return false;
  const data = block.data || {};
  return !data.id && !data.url && !data.link && !data.href && !data.name;
}

function attachmentBlockType(attachment) {
  const mimeType = String(
    attachment?.mimeType || attachment?.type || ""
  ).toLowerCase();
  const extension = String(
    attachment?.extension || attachment?.name?.split(".").pop() || ""
  ).toLowerCase();
  if (
    mimeType.startsWith("image/") ||
    ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(extension)
  )
    return "image";
  if (
    mimeType.startsWith("video/") ||
    ["mp4", "webm", "mov", "m4v", "avi"].includes(extension)
  )
    return "video";
  if (
    mimeType.startsWith("audio/") ||
    ["mp3", "wav", "ogg", "m4a", "aac", "flac"].includes(extension)
  )
    return "audio";
  return "file";
}

function normalizeNoteBlocks(blocks = []) {
  const filteredBlocks = blocks.filter((block) => !isEmptyFileBlock(block));
  const firstEmptyParagraphIndex = filteredBlocks.findIndex(
    (block) => block.type === "paragraph" && !block.data?.text?.trim()
  );
  if (firstEmptyParagraphIndex <= 0) return filteredBlocks;

  const nextBlocks = [...filteredBlocks];
  const [emptyParagraph] = nextBlocks.splice(firstEmptyParagraphIndex, 1);
  return [emptyParagraph, ...nextBlocks];
}

function buildNoteContentWithAttachments(note, attachments) {
  const currentContent =
    note.content && typeof note.content === "object" ? note.content : {};
  const currentBlocks = Array.isArray(currentContent.blocks)
    ? normalizeNoteBlocks(currentContent.blocks)
    : [];
  const firstEmptyParagraphIndex = currentBlocks.findIndex(
    (block) => block.type === "paragraph" && !block.data?.text?.trim()
  );
  const fileBlocks = attachments.map((attachment) => ({
    type: attachmentBlockType(attachment),
    data: {
      id: attachment.id,
      name: attachment.name,
      url: attachment.url,
      path: attachment.path,
      cdnUrl: attachment.cdnUrl,
      storageSource: attachment.storageSource,
      size: attachment.size,
      type: attachment.type,
      extension: attachment.extension,
    },
  }));
  const nextBlocks = [...currentBlocks];
  if (firstEmptyParagraphIndex >= 0) {
    nextBlocks.splice(firstEmptyParagraphIndex + 1, 0, ...fileBlocks);
  } else {
    nextBlocks.push(...fileBlocks);
  }

  return {
    ...currentContent,
    time: Date.now(),
    blocks: nextBlocks,
  };
}

function getNoteContentForEditor(note) {
  if (!note) return null;
  const currentContent =
    note.content && typeof note.content === "object" ? note.content : {};
  const currentBlocks = Array.isArray(currentContent.blocks)
    ? normalizeNoteBlocks(currentContent.blocks)
    : [];
  const existingFileIds = new Set(
    currentBlocks
      .filter((block) =>
        ["file", "image", "video", "audio"].includes(block.type)
      )
      .map((block) => block.data?.id || block.data?.url)
  );
  const missingAttachmentBlocks = (
    Array.isArray(note.attachments) ? note.attachments : []
  )
    .filter(
      (attachment) => !existingFileIds.has(attachment.id || attachment.url)
    )
    .map((attachment) => ({
      type: attachmentBlockType(attachment),
      data: {
        id: attachment.id,
        name: attachment.name,
        url: attachment.url,
        path: attachment.path,
        cdnUrl: attachment.cdnUrl,
        storageSource: attachment.storageSource,
        size: attachment.size,
        type: attachment.type,
        extension: attachment.extension,
      },
    }));

  if (!missingAttachmentBlocks.length) return note.content;
  return {
    ...currentContent,
    blocks: [...currentBlocks, ...missingAttachmentBlocks],
  };
}

async function handleNoteFileChange(event) {
  const input = event.target;
  const files = Array.from(input.files || []);
  input.value = "";
  await uploadNoteFiles(files);
}

async function uploadNoteFiles(files = []) {
  if (!files.length || !fileTargetNoteId.value) return;

  const note = notes.value.find((item) => item._id === fileTargetNoteId.value);
  if (!note) return;

  uploadingNoteFile.value = true;
  try {
    const validFiles = await checkFile(
      files,
      store.getters["settings/fileExtentions"] || []
    );
    if (!validFiles?.length) return;

    const uploadedAttachments = [];
    for (const file of validFiles) {
      const safeName = `${Date.now()}_${file.name}`.replace(
        /[\\/:*?"<>|]/g,
        "_"
      );
      const uploadedFile = await uploadToWasabi(
        file.data,
        `Notes/${note._id}/${safeName}`,
        getCompanyId()
      );
      if (!uploadedFile?.url) continue;
      uploadedAttachments.push({
        id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
        name: file.name,
        url: uploadedFile.url,
        path: uploadedFile.path || uploadedFile.url,
        cdnUrl: uploadedFile.cdnUrl || "",
        storageSource: uploadedFile.storageSource || "",
        size: file.data.size,
        type: file.fileType,
        mimeType: file.data.type || file.fileType || "",
        extension: file.name.split(".").pop()?.toLowerCase() || "",
        uploadedAt: new Date().toISOString(),
        uploadedBy: currentUser.value?._id || currentUser.value?.id || "",
      });
    }

    if (!uploadedAttachments.length) return;
    const nextAttachments = [
      ...(Array.isArray(note.attachments) ? note.attachments : []),
      ...uploadedAttachments,
    ];
    const nextContent = buildNoteContentWithAttachments(
      note,
      uploadedAttachments
    );
    const res = await apiRequest("put", `/api/v1/notes/${note._id}`, {
      attachments: nextAttachments,
      content: nextContent,
    });
    const updatedNote = res?.data?.data || {
      ...note,
      attachments: nextAttachments,
      content: nextContent,
    };
    updateNoteInLocal(updatedNote);
    noteDescriptionRef.value?.renderContent?.(
      getNoteContentForEditor(updatedNote)
    );
    window.dispatchEvent(
      new CustomEvent("note-updated", {
        detail: { note: updatedNote },
      })
    );
  } catch (err) {
    console.error("Upload note file error:", err);
  } finally {
    uploadingNoteFile.value = false;
    fileTargetNoteId.value = null;
  }
}

function getCompanyId() {
  const selectedCompany = store.getters["settings/selectedCompany"];
  return (
    selectedCompany?._id ||
    selectedCompany ||
    localStorage.getItem("selectedCompany") ||
    ""
  );
}

// Delete note
function deleteNote(note) {
  noteToDelete.value = note;
  deleteModalTitle.value = note.title || t("notes.untitled");
  showDeleteModal.value = true;
}

async function handleDeleteConfirm() {
  if (!noteToDelete.value) return;
  try {
    await apiRequest("delete", `/api/v1/notes/${noteToDelete.value._id}`);
    window.dispatchEvent(
      new CustomEvent("note-deleted", {
        detail: { noteId: noteToDelete.value._id },
      })
    );
    notes.value = notes.value.filter((n) => n._id !== noteToDelete.value._id);

    if (activeNoteId.value === noteToDelete.value._id) {
      activeNoteId.value = notes.value[0]?._id || null;
    }
  } catch (err) {
    console.error(err);
    alert(t("notes.failed_delete"));
  } finally {
    showDeleteModal.value = false;
    noteToDelete.value = null;
  }
}

// Pin / unpin
async function togglePin(note) {
  if (!note || !note._id) return;

  try {
    const nextPinValue = !note.is_pin;
    const res = await apiRequest("put", `/api/v1/notes/${note._id}`, {
      is_pin: nextPinValue,
    });
    const responseNote = res?.data?.data;
    if (!responseNote) return;
    const updatedNote = {
      ...responseNote,
      is_pin: nextPinValue,
    };

    const index = notes.value.findIndex((n) => n._id === note._id);
    if (index >= 0)
      notes.value[index] = { ...notes.value[index], ...updatedNote };
    window.dispatchEvent(
      new CustomEvent("note-pin-updated", {
        detail: { note: { ...note, ...updatedNote } },
      })
    );

    // Sort pin first
    notes.value.sort((a, b) => {
      if (a.is_pin && !b.is_pin) return -1;
      if (!a.is_pin && b.is_pin) return 1;
      return (
        new Date(b.updatedAt || b.createdAt) -
        new Date(a.updatedAt || a.createdAt)
      );
    });

    if (updatedNote.is_pin) activeNoteId.value = updatedNote._id;
  } catch (err) {
    console.error("Toggle pin error:", err);
    alert(t("notes.failed_update_pin"));
  }
}

// Update note title
async function updateNoteTitle(note) {
  if (!note || !note._id) return;

  try {
    const res = await apiRequest("put", `/api/v1/notes/${note._id}`, {
      title: note.title,
    });
    const updatedNote = res?.data?.data;
    if (!updatedNote) return;

    const index = notes.value.findIndex((n) => n._id === note._id);
    if (index >= 0)
      notes.value[index] = { ...notes.value[index], ...updatedNote };
    window.dispatchEvent(
      new CustomEvent("note-updated", {
        detail: { note: { ...note, ...updatedNote } },
      })
    );
  } catch (err) {
    console.error("Update note title error:", err);
    alert(t("notes.failed_update_title"));
  }
}
</script>

<style>
/* ======================
   Sidebar tổng thể
   ====================== */
.user-note-sidebar .sidebar-content {
  left: 50%;
  transform: translateX(-50%);
}
.user-note-sidebar .sidebar-body {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

/* ======================
   Layout tổng thể notes
   ====================== */
.notes-layout {
  display: flex;
  height: 100%;
}

/* ======================
   Danh sách note bên trái
   ====================== */
.note-list {
  width: 260px;
  border-right: 1px solid #eee;
  overflow-y: auto;
  background: #f9f9f9;
  border-radius: 12px 0 0 12px;
  padding: 8px 0;
}

/* Header danh sách note (chứa button add note) */
.note-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid #eee;
}

/* ======================
   Item note
   ====================== */
.note-item {
  padding: 10px 14px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s;
  position: relative;
}

.note-item:hover {
  background: #eaeaea;
}

.note-item.active {
  background: #d0e6ff;
  box-shadow: inset 2px 0 0 #007bff;
}

/* ======================
   Tiêu đề và pin icon
   ====================== */
.note-title {
  font-weight: 500;
  font-size: 14px;
  color: #333;
}
.note-item-pin-icon {
  width: 14px;
  height: 14px;
  position: absolute;
  top: 10px;
  right: 10px;
}

/* ======================
   Preview nội dung note
   ====================== */
.note-preview {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ======================
   Editor bên phải
   ====================== */
.note-editor {
  flex: 1;
  background: #fff;
  border-radius: 0 12px 12px 0;
  box-shadow: inset 0 0 0 1px #eee;
  overflow-y: auto;
}

/* Trạng thái khi chưa chọn note */
.empty-editor {
  color: #999;
  text-align: center;
  margin-top: 60px;
  font-size: 14px;
}

/* ======================
   Header editor (chứa title + action)
   ====================== */
.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background-color: #f0f0f0;
  border-bottom: 1px solid #ddd;
  height: 51px;
}

.editor-header div:first-child {
  font-weight: 500;
  font-size: 16px;
  color: #333;
}

/* ======================
   Action buttons (edit, delete, pin)
   ====================== */
.note-actions {
  display: flex;
  gap: 8px;
}

.note-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.note-actions img {
  width: 18px;
  height: 18px;
  cursor: pointer;
  filter: grayscale(100%);
  transition: filter 0.2s;
}

.note-actions img:hover {
  filter: grayscale(0%);
}

/* ======================
   Container editor padding tùy chỉnh
   ====================== */
.take-note-attachments {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 20px 20px;
}

.take-note-attachment-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e5eaf1;
  border-radius: 8px;
  background: #f8fafc;
  cursor: pointer;
  text-align: left;
}

.take-note-attachment-item:hover {
  background: #eef6ff;
  border-color: #cde3ff;
}

.take-note-attachment-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 500;
}

.take-note-attachment-size {
  flex: 0 0 auto;
  color: #8a94a6;
  font-size: 12px;
}

#editorjs {
  border: none !important;
}
</style>
